import { ChamberLabel } from "@logora/parliament.parliament_chamber.chamber_label";
import { MemberBox } from "@logora/parliament.parliament_member.member_box";
import { Box } from "@logora/parliament.ui.box";
import { Label } from "@logora/parliament.ui.label";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { Box as MuiBox, Stack, Typography } from "@mui/material";
import cx from "classnames";
import React, { useCallback, useEffect, useState } from "react";
import { useDataProvider, useRedirect } from "react-admin";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertButton } from "../alerts";
import { Show } from "../components/show/Show";
import { capitalizeFirstLetter } from "../formatters";
import { ActeLegislatifDetails } from "./ActeLegislatif/ActeLegislatifDetails";
import styles from "./DossierShow.module.scss";
import { Timeline } from "./Timeline/Timeline";
import { ALLOWED_CODE_ACTES } from "./actesDefinitions";
import { useMilestones } from "./hooks/useMilestones";

const DossierDetails = ({ resource }) => {
	const redirectTo = useRedirect();
	const dataProvider = useDataProvider();
	const [actesLegislatifs, setActesLegislatifs] = useState([]);
	const [allActesLegislatifs, setAllActesLegislatifs] = useState([]);
	const [searchParams] = useSearchParams();
	const navigate = useNavigate();
	const [selectedActe, setSelectedActe] = useState(null);
	const [acteRetraitAuteur, setActeRetraitAuteur] = useState(null);
	const [acteProcedureAccelere, setActeProcedureAccelere] = useState(null);
	const { milestones, steps, currentActe } = useMilestones(
		allActesLegislatifs,
		resource.uidDernierActe,
	);

	const fetchActesLegislatifs = useCallback(async () => {
		try {
			const { data } = await dataProvider.getList("actesLegislatifs", {
				pagination: { page: 1, perPage: 1000 },
				sort: { field: "dateActe", order: "asc" },
				include: ["pointOdj.agendaRef"],
				filter: { dossierRefUid: resource.uid },
			});

			const filteredActesLegislatifs = data
				.filter((a) => Object.keys(ALLOWED_CODE_ACTES).includes(a.codeActe))
				.filter(
					(c, index, self) =>
						index ===
						self.findIndex(
							(t) =>
								t.codeActe === c.codeActe &&
								t.uid.substring(0, 3) === c.uid.substring(0, 3),
						),
				)
				.sort(
					(a, b) =>
						ALLOWED_CODE_ACTES[a.codeActe]?.order -
						ALLOWED_CODE_ACTES[b.codeActe]?.order,
				);

			setActesLegislatifs(filteredActesLegislatifs);
			setAllActesLegislatifs(data);

			const codeActesRetraitAuteur = [
				"ANLUNI-RTRINI",
				"AN1-RTRINI",
				"AN2-RTRINI",
				"SNLUNI-RTRINI",
				"SN1-RTRINI",
				"SN2-RTRINI",
			];
			if (!acteRetraitAuteur) {
				setActeRetraitAuteur(
					data.find((acte) => codeActesRetraitAuteur.includes(acte.codeActe)),
				);
			}

			const codeActesProcedureAccelere = [
				"AN1-PROCACC",
				"AN2-PROCACC",
				"SN1-PROCACC",
				"SN2-PROCACC",
				"ANLUNI-PROCACC",
				"SNLUNI-PROCACC",
			];
			if (!acteProcedureAccelere) {
				setActeProcedureAccelere(
					data.find((acte) =>
						codeActesProcedureAccelere.includes(acte.codeActe),
					),
				);
			}
		} catch (error) {
			console.error("Error fetching actesLegislatifs:", error);
		}
	}, [dataProvider, resource.uid, acteProcedureAccelere, acteRetraitAuteur]);

	const handleChangeActe = (acteLegislatifUid) => {
		if (acteLegislatifUid) {
			const acteLegislatif = actesLegislatifs.find(
				(acte) => acte.uid === acteLegislatifUid,
			);
			setSelectedActe(acteLegislatif);

			const newParams = new URLSearchParams(searchParams);
			newParams.set("acteLegislatif", acteLegislatifUid);
			navigate(`?${newParams.toString()}`, { replace: true });
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		fetchActesLegislatifs();
	}, [resource.uid, fetchActesLegislatifs]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (currentActe && !selectedActe) {
			const selectedActeUid = searchParams.get("acteLegislatif");
			let acteLegislatif = null;
			if (selectedActeUid) {
				acteLegislatif = actesLegislatifs.find(
					(acte) => acte.uid === selectedActeUid,
				);
			}
			if (!acteLegislatif) {
				acteLegislatif = currentActe;
			}
			setSelectedActe(acteLegislatif);
		}
	}, [currentActe]);

	const auteur =
		resource.acteurPrincipalRef || resource.documentDeposeRef?.auteurPrincipal;

	return (
		<div className={styles.dossier}>
			<div className={styles.header}>
				{resource.chambre && <ChamberLabel chamber={resource.chambre} />}
				<Label className={styles.label} text={resource.typeInitiative} />

				<MuiBox className={styles.alertButton}>
					<AlertButton targetClass={"Dossier"} targetId={resource.uid} />
				</MuiBox>
			</div>
			<div className={styles.title}>{resource.titre}</div>
			<div className={styles.presentation}>
				{resource.rapporteurs.length > 0 ? (
					<div className={styles.box}>
						<Box
							title={
								resource.rapporteurs.length === 1
									? "Rapporteur"
									: `Rapporteurs (${resource.rapporteurs.length})`
							}
							className={styles.boxContainer}
						>
							{resource.rapporteurs.map((rapporteur) => {
								return (
									<MemberBox
										key={rapporteur.acteurRefUid}
										firstName={rapporteur.acteurRef?.prenom}
										lastName={rapporteur.acteurRef?.nom}
										imageUrl={rapporteur.acteurRef?.urlImage}
										size="small"
										layout="oneLine"
										parliamentGroup={{
											groupName:
												rapporteur.acteurRef?.groupeParlementaire
													?.libelleAbrege,
											color:
												rapporteur.acteurRef?.groupeParlementaire
													?.couleurAssociee,
										}}
										className={styles.auteur}
										chamber={rapporteur.acteurRef?.chambre}
										onClick={() =>
											redirectTo(
												`/parliament/acteurs/${rapporteur.acteurRefUid}`,
											)
										}
									/>
								);
							})}
						</Box>
					</div>
				) : (
					auteur && (
						<div className={styles.box}>
							<Box title="Auteur" className={styles.boxContainer}>
								<MemberBox
									firstName={auteur?.prenom}
									lastName={auteur?.nom}
									imageUrl={auteur?.urlImage}
									size="small"
									layout="oneLine"
									parliamentGroup={{
										groupName: auteur?.groupeParlementaire?.libelleAbrege,
										color: auteur?.groupeParlementaire?.couleurAssociee,
									}}
									className={styles.auteur}
									chamber={auteur?.chambre}
									onClick={() =>
										redirectTo(`/parliament/acteurs/${auteur.uid}`)
									}
								/>
							</Box>
						</div>
					)
				)}
				<div className={styles.box}>
					<Box
						title={
							resource.commissionANRef && resource.commissionSNRef
								? "Commissions"
								: "Commission"
						}
						className={styles.boxContainer}
					>
						{resource.commissionANRef && (
							<div className={styles.commissionBox}>
								<ChamberLabel chamber={"AN"} short className={styles.smaller} />
								{/* biome-ignore lint/a11y/useKeyWithClickEvents: no need to implement key events */}
								<div
									className={styles.link}
									onClick={() =>
										redirectTo(
											`/parliament/organes/${resource.commissionANRefUid}`,
										)
									}
								>
									{resource.commissionANRef?.libelleTronque ||
										resource.commissionANRef?.libelle}
								</div>
							</div>
						)}
						{resource.commissionSNRef && (
							<div className={styles.commissionBox}>
								<ChamberLabel chamber={"SN"} short className={styles.smaller} />
								{/* biome-ignore lint/a11y/useKeyWithClickEvents: no need to implement key events */}
								<div
									className={styles.link}
									onClick={() =>
										redirectTo(
											`/parliament/organes/${resource.commissionSNRefUid}`,
										)
									}
								>
									{resource.commissionSNRef?.libelleTronque ||
										resource.commissionSNRef?.libelle}
								</div>
							</div>
						)}
						{!resource.commissionANRef && !resource.commissionSNRef && (
							<i>Pas de commission définie</i>
						)}
					</Box>
				</div>
				<div className={styles.box}>
					<Box
						title={
							resource.dateDepot && resource.dateDernierActe ? "Dates" : "Date"
						}
						className={styles.boxContainer}
					>
						<div className={styles.boxBody}>
							{resource.dateDepot && (
								<Stack direction="row" gap={1} alignItems="center">
									<b>Dépôt:</b>

									{capitalizeFirstLetter(
										new Date(resource.dateDepot).toLocaleDateString("fr", {
											year: "numeric",
											month: "long",
											day: "numeric",
											weekday: "long",
										}),
									)}
								</Stack>
							)}

							{resource.dateDernierActe && (
								<Stack direction="row" gap={1} alignItems="center">
									<b>Dernière activité:</b>

									{capitalizeFirstLetter(
										new Date(resource.dateDernierActe).toLocaleDateString(
											"fr",
											{
												year: "numeric",
												month: "long",
												day: "numeric",
												weekday: "long",
											},
										),
									)}
								</Stack>
							)}
						</div>
					</Box>
				</div>
			</div>
			{acteRetraitAuteur && (
				<div className={styles.extraLabel}>
					<Label
						text={`Ce texte a été retiré par son auteur le ${new Date(
							acteRetraitAuteur.dateActe,
						).toLocaleDateString("fr", {
							year: "numeric",
							month: "long",
							day: "numeric",
						})}`}
						colorType="error"
						variantsObject={{ cancel: "error" }}
					/>
				</div>
			)}
			{acteProcedureAccelere && (
				<div className={styles.extraLabel}>
					<Label
						text={`Le gouvernement a engagé la procédure accélérée le ${new Date(
							acteProcedureAccelere.dateActe,
						).toLocaleDateString("fr", {
							year: "numeric",
							month: "long",
							day: "numeric",
						})}`}
						colorType={"neutral"}
						variantsObject={{
							info: "neutral",
						}}
					/>
				</div>
			)}
			<div className={styles.dossierContainer}>
				<div className={styles.timelineWrapper}>
					{milestones &&
						selectedActe &&
						resource.typeInitiative !== "Rapport d'information" && (
							<Timeline
								milestones={milestones}
								selectedActeUid={selectedActe?.uid}
								onChangeActe={handleChangeActe}
							/>
						)}
				</div>
				<div className={styles.navigationContainer}>
					<div className={cx(styles.navigationItem, styles.previousButton)}>
						{steps[0] !== selectedActe?.uid && (
							<button
								className={styles.stepButton}
								onClick={() =>
									handleChangeActe(steps[steps.indexOf(selectedActe?.uid) - 1])
								}
								type="button"
							>
								<>
									<ChevronLeft fontSize="small" />
									<span>Étape précédente</span>
								</>
							</button>
						)}
					</div>
					<div className={styles.navigationItem}>
						<div className={styles.stepTitle}>
							{ALLOWED_CODE_ACTES[selectedActe?.codeActe]?.title ||
								selectedActe?.nomCanonique}
						</div>
					</div>
					<div className={cx(styles.navigationItem, styles.nextButton)}>
						{steps[steps.length - 1] !== selectedActe?.uid && (
							<button
								className={styles.stepButton}
								onClick={() =>
									handleChangeActe(steps[steps.indexOf(selectedActe?.uid) + 1])
								}
								type="button"
							>
								<span>Étape suivante</span>
								<ChevronRight fontSize="small" />
							</button>
						)}
					</div>
				</div>
				{selectedActe && (
					<ActeLegislatifDetails
						acteLegislatif={selectedActe}
						acteDefinition={ALLOWED_CODE_ACTES[selectedActe.codeActe]}
						actesLegislatifs={allActesLegislatifs}
						exposeMotifsTexte={resource.documentDeposeRef?.exposeMotifsTexte}
					/>
				)}
			</div>
		</div>
	);
};

export const DossierShow = () => {
	return (
		<Show
			resource={"dossiers"}
			includes={[
				"acteurPrincipalRef.groupeParlementaire",
				"acteurPrincipalRef.mandatPrincipal",
				"documentDeposeRef.auteurPrincipal.groupeParlementaire",
				"commissionANRef",
				"commissionSNRef",
				"rapporteurs.acteurRef.groupeParlementaire",
			]}
		>
			<DossierDetails />
		</Show>
	);
};
